import * as FlatModels from '@medsurf/flat-models';
import { KeymapSelftest } from '../elements/marker/keymapSelftest';
import { Position } from './position';
import { Freeform } from '../elements/freeform/freeform';
import { Tool } from './tool';

let _preventMoving;

/**
 * MarkerTool class
 */
export class MarkerTool extends Tool {
  /**
   * Members
   */
  private _hitOptions: { fill: boolean; stroke: boolean; tolerance: number; segments: boolean };
  private sequenceId: number;
  private layerId: number;
  private _cmdPressed: boolean;
  private _ctrlPressed: boolean;
  private selfMarker: any;
  private _segment: any;
  private _lockimage: any;
  private _lastPoint: any;
  private _hitResults: any;
  private _hitResult: any;
  private self: this;
  private _startPoints: any;
  private _isMouseDown: boolean;

  /**
   * Constructor
   *
   * @param localPaper
   * @param container
   * @param $window
   * @param slide
   * @param _command
   * @param _imageScale
   * @param _imageOffset
   */
  public constructor(localPaper,
                     container,
                     private $window,
                     private slide,
                     private _command,
                     private _imageScale = 1,
                     private _imageOffset?) {
    super('MarkerTool', localPaper, null, null, null, container);
    this._hitOptions = {
      segments: true,
      stroke: true,
      fill: true,
      tolerance: 5
    };
    this._init();
    this.sequenceId = 0;
    this.layerId = 0;
  }

  /**
   * Init
   *
   * @protected
   */
  protected _init() {
    super._init();
    this._cmdPressed = false;
    this._ctrlPressed = false;
    this.tool['self'] = this;
    this._tool.on({
      mousemove: this._handleMouseMove,
      mousedown: this._handleMouseDown,
      mousedrag: this._handleMouseDrag,
      mouseup: this._handleMouseUp
    });
    this.$window.selfMarker = this;
    this.$window.addEventListener('keydown', this._keyDown, false);
    this.$window.addEventListener('keyup', this._keyUp, false);
  }

  /**
   * Event kesy down
   *
   * @param event
   * @protected
   */
  protected _keyDown(event) {
    if (event.keyCode === 91) {
      this.selfMarker._cmdPressed = true;
    }
    if (event.keyCode === 17) {
      this.selfMarker._ctrlPressed = true;
    }
    if (event.keyCode === 65 && (this.selfMarker._cmdPressed || event.ctrlKey)) {
      event.stopPropagation();
      event.preventDefault();
      this.selfMarker.emit('selectAllMarkers');
    }
    this.selfMarker._updateSelectedItems(event);
  }

  /**
   * Command move label
   *
   * @param marker
   * @param value
   * @protected
   */
  protected _commandMoveLabel(marker,
                              value) {
    this._command.doCommand(function (data) {
      marker.model.setSource(value);
      return data;
    }, 'moveLabel');
    this.emit('updateMarker');
  }

  /**
   * Command move target
   *
   * @param marker
   * @param index
   * @param value
   * @protected
   */
  protected _commandMoveTarget(marker,
                               index,
                               value) {
    this._command.doCommand(function (data) {
      marker.model.fields.target[index].x += value.x;
      marker.model.fields.target[index].y += value.y;
      return data;
    }, 'moveTarget');
    this.emit('updateMarker');
  }

  /**
   * Command move image
   *
   * @param image
   * @param value
   * @protected
   */
  protected _commandMoveImage(image,
                              value) {
    this._command.doCommand(function (data) {
      image.offset = value;
      return data;
    }, 'moveImage');
    this.emit('updateMarker');
  }

  /**
   * Command move fork
   *
   * @param marker
   * @param value
   * @protected
   */
  protected _commandMoveFork(marker,
                             value) {
    this._command.doCommand(function (data) {
      marker.model.fields.point.fork = value;
      return data;
    }, 'moveFork');
    this.emit('updateMarker');
  }

  /**
   * Command move pointer
   *
   * @param marker
   * @param value
   * @protected
   */
  protected _commandMovePointer(marker,
                                value) {
    this._command.doCommand(function (data) {
      for (const target of marker.model.fields.target) {
        target.x += value.x;
        target.y += value.y;
      }
      if (marker.model.fields.label) {
        marker.model.fields.source.x += value.x;
        marker.model.fields.source.y += value.y;
      }
      if (marker.model.fields.point && marker.model.fields.point.fork) {
        marker.model.fields.point.fork.x += value.x;
        marker.model.fields.point.fork.y += value.y;
      }
      return data;
    }, 'movePointer');
    this.emit('updateMarker');
  }

  /**
   * Command move keymap
   *
   * @param keymap
   * @param value
   * @protected
   */
  protected _commandMoveKeymap(keymap,
                               value) {
    if (keymap instanceof KeymapSelftest) {
      this._command.doCommand(function (data) {
        // TODO keymap.model.addSelftest(value);
        return data;
      }, 'moveKeymapSelftest');
    } else {
      this._command.doCommand(function (data) {
        keymap.model.addSource(value);
        return data;
      }, 'moveKeymap');
    }
    this.emit('updateMarker');
  }

  /**
   * Command move object
   *
   * @param object
   * @param value
   * @param relativePos
   * @protected
   */
  protected _commandMoveObject(object,
                               value,
                               relativePos) {
    this._command.doCommand(function (data) {
      object.model?.addSource(value, relativePos);
      return data;
    }, 'moveObject');
    this.emit('updateMarker');
  }

  /**
   * Command move segment
   *
   * @param freeform
   * @param relativeDelta
   * @param relativePos
   * @protected
   */
  protected _commandMoveSegment(freeform,
                                relativeDelta,
                                relativePos) {
    this._command.doCommand(function (data) {
      freeform.model.moveSegment(relativeDelta, relativePos);
      return data;
    }, 'moveSegment');
    this.emit('updateMarker');
  }

  /**
   * Command resize freeform
   *
   * @param freeform
   * @param value
   * @protected
   */
  protected _commandResizeFreeform(freeform,
                                   value) {
    this._command.doCommand(function (data) {
      freeform.model.resize(value);
      return data;
    }, 'resizeFreeform');
    this.emit('updateMarker');
  }

  /**
   * Command move multiple items
   *
   * @param elements
   * @param value
   * @protected
   */
  protected _commandMoveMultibleItems(elements,
                                      value) {
    this._command.doCommand(function (data) {
      for (const element of elements) {
        if (element instanceof Freeform) {
          // TODO element.model.addSource(value);
        } else {
          element.model.fields.source.x += value.x;
          element.model.fields.source.y += value.y;
          if (element.model.fields.target) {
            for (const target of element.model.fields.target) {
              target.x += value.x;
              target.y += value.y;
            }
          }
          if (element.model.fields.point && element.model.fields.point.fork) {
            element.model.fields.point.fork.x += value.x;
            element.model.fields.point.fork.y += value.y;
          }
        }
      }
      return data;
    }, 'moveMultibleItems');
    this.emit('updateMarker');
  }

  /**
   * Command move virtualpointer
   *
   * @param virtualPointer
   * @param value
   * @protected
   */
  protected _commandMoveVirtualPointer(virtualPointer,
                                       value) {
    this._command.doCommand(function (data) {
      virtualPointer.model.offset.x += value.x;
      virtualPointer.model.offset.y += value.y;
      return data;
    }, 'moveVirtualPointer');
    this.emit('updateVirtualPointer');
  }

  /**
   * Update selected items
   *
   * @param event
   * @protected
   */
  protected _updateSelectedItems(event) {
    const step = (event.shiftKey) ? 10 : 1;
    let stepX = 0,
      stepY = 0;
    if (event.keyCode === 38) {
      stepY = -step;
    }
    if (event.keyCode === 40) {
      stepY = step;
    }
    if (event.keyCode === 39) {
      stepX = step;
    }
    if (event.keyCode === 37) {
      stepX = -step;
    }
    if (this._container.selection.length > 0) {
      this.moveMultibleItems(stepX, stepY);
    }
  }

  /**
   * Move multiple items
   *
   * @param stepX
   * @param stepY
   */
  public moveMultibleItems(stepX,
                           stepY) {
    const selectedItems = this._container.selection,
      relativeDelta = this._container.getRelativeDelta(
        stepX,
        stepY
      );
    this._commandMoveMultibleItems(selectedItems, relativeDelta);
  }

  /**
   * Event keyup
   *
   * @protected
   */
  protected _keyUp() {
    this.selfMarker._cmdPressed = false;
  }

  /**
   * Handle mouse over
   *
   * @param event
   * @protected
   */
  protected _handleMouseMove(event) {
    this._segment = null;
    this._lastPoint = this.self._localPaper.view.projectToView(event.point);
    this._hitResults = this.self._localPaper.project.hitTestAll(event.point, Object.assign({hidden: true}, this.self._hitOptions));
    this._hitResult = this._hitResults
      .find(h => h.item && h.item.marker && (h.item.marker._model.annotation).type === FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker);
    if (!this._hitResult) {
      this._hitResult = this._hitResults[0];
    }
    this._segment = this._hitResult;

    if (!this._hitResult) {
      this.self.emit('overImage', {event});
      this.self.emit('unoverMarker');
      return;
    }
    const marker = (this._hitResult.item &&
      this._hitResult.item.marker &&
      (this._hitResult.item.marker._model.annotation).type === FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker) ?
      this._hitResult.item.marker :
      null;
    const keymap = (this._hitResult.item && this._hitResult.item.keymap) ? this._hitResult.item.keymap : null;
    let freeform = (this._hitResult.item && this._hitResult.item.freeform) ? this._hitResult.item.freeform : null;
    const virtualPointer = (this._hitResult.item && this._hitResult.item.sprite) ? this._hitResult.item.sprite : null;

    // TODO: Refactoring; dirty hack for freeform labels
    // dirty hack for freeform labels, freeform label inherits from markerElement class (class variable is this._marker)
    freeform = (this._hitResult.item &&
      this._hitResult.item.marker &&
      (this._hitResult.item.marker._model.annotation).type === FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm) ?
      this._hitResult.item.marker :
      freeform;

    switch (this._hitResult.type) {
      case 'fill':
        if (!(marker || keymap || freeform)) {
          this.self.emit('unoverMarker');
          return;
        }
        this._hitResult.item.parent.bringToFront();
        if (marker && marker.model && marker.model.fields && marker.model.fields.target && marker.model.fields.target.length > 0) {
          this.self._startPoints = this.self._container.getAbsoluteCoords(
            marker.model.fields.target[0].x,
            marker.model.fields.target[0].y,
            this._imageOffset,
            this._imageScale
          );
        }
        if (marker) {
          this.self.emit('overMarker', {item: marker});
        } else if (keymap) {
          this.self.emit('overKeymap', {item: keymap});
        } else if (freeform) {
          this.self.emit('overFreeform', {item: freeform});
        }
        break;

      case 'pixel':
        if (virtualPointer) {
          if (virtualPointer.isMovable) {
            this.self.emit('overVirtualPointer', {item: virtualPointer});
          } else {
            this.self.emit('unoverMarker');
          }
        } else {
          const image = this.self._container.getElementById('image');
          if (image) {
            this.self.emit('overImage', {item: image});
          } else {
            this.self.emit('unoverMarker');
          }
        }
        break;

      case 'stroke':
      case 'segment':
        if (!(marker || keymap || freeform)) {
          this.self.emit('unoverMarker');
          return;
        }
        this._hitResult.item.parent.bringToFront();
        if (marker) {
          this.self.emit('overMarker', {item: marker});
        } else if (keymap) {
          this.self.emit('overKeymap', {item: keymap});
        } else if (freeform) {
          this.self.emit('overFreeform', {item: freeform});
        }
        break;
      default:
        if (marker && marker.isMovable) {
          this.self.emit('overMarker', {item: this._hitResult.item.class});
        } else {
          this.self.emit('unoverMarker');
        }
        break;
    }
  }

  /**
   * Handle mouse down
   *
   * @param event
   * @protected
   */
  protected _handleMouseDown(event) {
    this._segment = null;
    this._lastPoint = this.self._localPaper.view.projectToView(event.point);
    this._hitResults = this.self._localPaper.project.hitTestAll(event.point, this.self._hitOptions);
    this._hitResult = this._hitResults
      .find(h => h.item && h.item.marker && (h.item.marker._model.annotation).type === FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker);
    if (!this._hitResult) {
      this._hitResult = this._hitResults[0];
    }
    this._segment = this._hitResult;

    const addToSelection = !!event.event.shiftKey;
    if (!this._hitResult) {
      this.self.emit('selectImage', {event});
      this.self.emit('unselectMarker');
      return;
    }
    if (this.self._container.allSelected && this._hitResult.type !== 'fill') {
      return;
    }
    if (this.self._container.selection.length > 1 && (this._hitResult.type !== 'fill' && this._hitResult.type !== 'pixel')) {
      return;
    }
    const marker = (this._hitResult.item && this._hitResult.item.marker &&
      this._hitResult.item.marker._model.annotation.type === FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker) ?
      this._hitResult.item.marker :
      null;
    const keymap = (this._hitResult.item && this._hitResult.item.keymap) ? this._hitResult.item.keymap : null;
    let freeform = (this._hitResult.item && this._hitResult.item.freeform) ? this._hitResult.item.freeform : null;
    const virtualPointer = (this._hitResult.item && this._hitResult.item.sprite) ? this._hitResult.item.sprite : null;

    // TODO: Refactoring; dirty hack for freeform labels
    // dirty hack for freeform labels, freeform label inherits from markerElement class (class variable is this._marker)
    freeform = (this._hitResult.item &&
      this._hitResult.item.marker &&
      (this._hitResult.item.marker._model.annotation).type === FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm) ?
      this._hitResult.item.marker :
      freeform;

    switch (this._hitResult.type) {
      case 'fill':
        if (!(marker || keymap || freeform)) {
          this.self.emit('unselectMarker');
          return;
        }
        this._hitResult.item.parent.bringToFront();
        if (marker && marker.model && marker.model.fields && marker.model.fields.target && marker.model.fields.target.length > 0) {
          this.self._startPoints = this.self._container.getAbsoluteCoordsMarkerTool(
            marker.model.fields.target[0].x,
            marker.model.fields.target[0].y
          );
        }
        if (marker) {
          this.self.emit('unselectMarker');
          this.self.emit('selectMarker', {
            item: marker,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        } else if (keymap) {
          this.self.emit('unselectMarker');
          this.self.emit('selectKeymap', {
            item: keymap,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        } else if (freeform) {
          this.self.emit('unselectMarker');
          this.self.emit('selectFreeform', {
            item: freeform,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        }
        break;

      case 'pixel':
        if (virtualPointer) {
          if (virtualPointer.isMovable) {
            this.self.emit('unselectMarker');
            this.self.emit('selectVirtualPointer', {
              item: virtualPointer,
              event: event,
              add: addToSelection,
              hitResult: this._hitResult
            });
          } else {
            this.self.emit('unselectMarker');
          }
        } else {
          const image = this.self._container.getElementById('image');
          if (image) {
            this.self.emit('unselectMarker');
            this.self.emit('selectImage', {
              item: image,
              event: event
            });
          }
          this.self.emit('unselectMarker');
        }
        break;

      case 'stroke':
      case 'segment':
        if (!(marker || keymap || freeform)) {
          return;
        }
        this._hitResult.item.parent.bringToFront();
        if (marker) {
          this.self.emit('unselectMarker');
          this.self.emit('selectMarker', {
            item: marker,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        } else if (keymap) {
          this.self.emit('unselectMarker');
          this.self.emit('selectKeymap', {
            item: keymap,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        } else if (freeform) {
          this.self.emit('unselectMarker');
          this.self.emit('selectFreeform', {
            item: freeform,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        }
        break;
      default:
        if (marker && marker.isMovable) {
          this.self.emit('unselectMarker');
          this.self.emit('selectMarker', {
            item: this._hitResult.item.class,
            event: event,
            add: addToSelection,
            hitResult: this._hitResult
          });
        } else {
          this.self.emit('unselectMarker');
        }
        break;
    }
  }

  /**
   * Handle mouse drag
   *
   * @param event
   * @protected
   */
  protected _handleMouseDrag(event) {
    if (!this._hitResult) {
      return;
    }
    const marker = (this._hitResult.item && this._hitResult.item.marker) ? this._hitResult.item.marker : null;
    const keymap = (this._hitResult.item && this._hitResult.item.keymap) ? this._hitResult.item.keymap : null;
    let freeform = (this._hitResult.item && this._hitResult.item.freeform) ? this._hitResult.item.freeform : null;
    const virtualPointer = (this._hitResult.item && this._hitResult.item.sprite) ? this._hitResult.item.sprite : null;

    // TODO: Refactoring; dirty hack for freeform labels
    // dirty hack for freeform labels, freeform label inherits from markerElement class (class variable is this._marker)
    freeform = (this._hitResult.item &&
      this._hitResult.item.marker &&
      (this._hitResult.item.marker._model.annotation).type === FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm) ?
      this._hitResult.item.marker :
      freeform;

    if (this.self._container.selection.length > 1) {
      this.self.moveMultibleItems(event.delta.x, event.delta.y);
      return;
    }
    switch (this._hitResult.type) {
      case 'stroke':
        if (this._segment.item.data.selectionType === 'label') {
          if (marker) {
            this.self.moveLabel(marker, event);
          }
        } else if (this._segment.item.data.selectionType === 'segment') {
          if (freeform) {
            this.self.moveSegment(freeform, event, this._hitResult.item.positionInPath);
          }
        } else if (this._segment.item.data.selectionType === 'resize') {
          this.self.resizeFreeform(freeform, event);
        } else if (keymap) {
          this.self.moveKeymap(keymap, event);
        } else if (freeform) {
          this.self.moveObject(freeform, event);
        } else {
          this.self.movePointer(marker, event);
        }
        break;

      case 'fill':
        if (this._segment.item.data.selectionType === 'label') {
          this.self.moveLabel(marker, event);
        } else if (this._segment.item.data.selectionType === 'fork') {
          this.self.moveFork(marker, event);
        } else if (this._segment.item.data.selectionType === 'segment') {
          if (freeform) {
            this.self.moveSegment(freeform, event, this._hitResult.item.positionInPath);
          }
        } else if (this._segment.item.data.selectionType === 'resize') {
          this.self.resizeFreeform(freeform, event);
        } else if (keymap) {
          this.self.moveKeymap(keymap, event);
        } else if (freeform) {
          this.self.moveObject(freeform, event, this._hitResult.item.positionInPath);
        } else {
          if (marker.model.fields.target && marker.model.fields.target.length > 0 && Number.isFinite(this._segment.item.data.index)) {
            this.self.moveTarget(marker, event, this._segment.item.data.index);
          }
        }
        break;

      case 'pixel':
        if (virtualPointer && virtualPointer.isMovable) {
          this.self.moveVirtualPointer(virtualPointer, event);
        } else if (this.self._container.getElementById('image').isMovable) {
          this.self.moveImage(event);
        }

        break;

      case 'segment':
        if (this._segment.item.data.selectionType === 'fork') {
          this.self.moveFork(marker, event);
        } else if (this._segment.item.data.selectionType === 'label') {
          this.self.moveLabel(marker, event);
        } else if (freeform) {
          if (freeform && freeform._model &&
            (freeform._model.annotation.shape === 'free' || freeform._model.annotation.shape === 'line')) {
            this.self.moveSegment(freeform, event, this._hitResult.item.positionInPath);
          } else {
            this.self.resizeFreeform(freeform, event);
          }
        } else {
          if (marker.model.fields.target && marker.model.fields.target.length > 0 && Number.isFinite(this._segment.item.data.index)) {
            this.self.moveTarget(marker, event, this._segment.item.data.index);
          }
        }
        break;
    }
  }

  /**
   * Move label
   *
   * @param marker
   * @param event
   */
  public moveLabel(marker,
                   event) {
    let relativeCoords;
    const absoluteCoords = this._container.getAbsoluteCoordsMarkerTool(
      marker.model.fields.source.x,
      marker.model.fields.source.y
    );
    const delta = event.pointerType === 'mouse' ? this._container.getAbsoluteDelta(event.delta.x, event.delta.y) : event.delta;
    if (event.event.shiftKey) {
      if (marker.model.fields.point && marker.model.fields.point.fork) {
        const absoluteCoordsFork = this._container.getAbsoluteCoordsMarkerTool(
          marker.model.fields.point.fork.x,
          marker.model.fields.point.fork.y
        );
        relativeCoords = this._snapToAngle(absoluteCoordsFork, delta, absoluteCoords);
      } else {
        relativeCoords = this._snapToAngle(this._startPoints, delta, absoluteCoords);
      }
    } else {
      relativeCoords = this._container.getRelativeCoords(
        absoluteCoords.x + delta.x,
        absoluteCoords.y + delta.y
      );
    }
    this._commandMoveLabel(marker, relativeCoords);
  }

  /**
   * Move Fork
   *
   * @param marker
   * @param event
   */
  public moveFork(marker,
                  event) {
    if (_preventMoving) {
      return;
    }
    let relativeCoords;

    const delta = event.pointerType === 'mouse' ? this._container.getAbsoluteDelta(event.delta.x, event.delta.y) : event.delta;

    if (!marker.model.fields.point) {
      marker.model.fields.point = {};
    }
    if (!marker.model.fields.point.fork) {
      marker.model.fields.point.fork = (JSON.parse(JSON.stringify(marker.model.fields.source)));
    }
    const absoluteCoords = this._container.getAbsoluteCoordsMarkerTool(
      marker.model.fields.point.fork.x,
      marker.model.fields.point.fork.y
    );
    relativeCoords = this._container.getRelativeCoords(
      absoluteCoords.x + delta.x,
      absoluteCoords.y + delta.y
    );
    if (event.event.shiftKey) {
      relativeCoords = this._snapToAngle(this._startPoints, delta, absoluteCoords);
    }
    const absoluteSourceCoords = this._container.getAbsoluteCoordsMarkerTool(
      marker.model.fields.source.x,
      marker.model.fields.source.y
    );
    if (Position.distance(absoluteCoords, absoluteSourceCoords) < 10 &&
      Position.distance(marker.model.fields.point.fork, marker.model.fields.source) >
      Position.distance(relativeCoords, marker.model.fields.source)) {
      relativeCoords = undefined;
      _preventMoving = true;
    }
    this._commandMoveFork(marker, relativeCoords);
  }

  /**
   * Move target
   *
   * @param marker
   * @param event
   * @param index
   */
  public moveTarget(marker,
                    event,
                    index) {
    const relativeDelta = this._container.getRelativeDelta(
      event.delta.x,
      event.delta.y
    );
    this._commandMoveTarget(marker, index, relativeDelta);
  }

  /**
   * Move pointer
   *
   * @param marker
   * @param event
   */
  public movePointer(marker,
                     event) {
    const relativeDelta = this._container.getRelativeDelta(
      event.delta.x,
      event.delta.y
    );
    this._commandMovePointer(marker, relativeDelta);
  }

  /**
   * Move keymap
   *
   * @param keymap
   * @param event
   */
  public moveKeymap(keymap,
                    event) {
    const relativeDelta = this._container.getRelativeDelta(
      event.delta.x,
      event.delta.y
    );
    this._commandMoveKeymap(keymap, relativeDelta);
  }

  /**
   * Move object
   *
   * @param object
   * @param event
   * @param index
   */
  public moveObject(object,
                    event,
                    index?) {
    const relativeDelta = this._container.getRelativeDelta(
      event.delta.x,
      event.delta.y
    );
    this._commandMoveObject(object, relativeDelta, index);
  }

  /**
   * Move segment
   *
   * @param freeform
   * @param event
   * @param index
   */
  public moveSegment(freeform,
                     event,
                     index) {
    const relativeDelta = this._container.getRelativeDelta(
      event.delta.x,
      event.delta.y
    );
    this._commandMoveSegment(freeform, relativeDelta, index);
  }

  /**
   * Resize freeform
   *
   * @param freeform
   * @param event
   */
  public resizeFreeform(freeform,
                        event) {
    const relativeDelta = this._container.getRelativeDelta(
      event.delta.x,
      event.delta.y
    );

    if (event.event.shiftKey) {
      relativeDelta.y = relativeDelta.x;
    }
    this._commandResizeFreeform(freeform, relativeDelta);
  }

  /**
   * Move image
   *
   * @param event
   */
  public moveImage(event) {
    const absoluteCoords = this._container.getAbsoluteCoords(
      this.slide.fields.image[this.sequenceId].offset.x,
      this.slide.fields.image[this.sequenceId].offset.y,
      this._imageOffset,
      this._imageScale
    );
    const relativeCoords = this._container.getRelativeCoords(
      absoluteCoords.x + event.delta.x,
      absoluteCoords.y + event.delta.y
    );
    this._commandMoveImage(this.slide.fields.image[this.sequenceId], relativeCoords);
  }

  /**
   * Move virtualpointer
   *
   * @param virtualPointer
   * @param event
   */
  public moveVirtualPointer(virtualPointer,
                     event) {
    const relativeDelta = this._container.getAbsoluteDelta(
      event.delta.x,
      event.delta.y
    );
    this._commandMoveVirtualPointer(virtualPointer, relativeDelta);
  }

  /**
   * Snap to angle
   *
   * @param startPoints
   * @param absoluteCoords
   * @param delta
   * @returns {{x: number, y: number}}
   * @protected
   */
  protected _snapToAngle(startPoints,
                         absoluteCoords,
                         delta) {
    const startX = startPoints.x,
      startY = startPoints.y,
      x2 = (absoluteCoords.x + delta.x) - startX,
      y2 = (absoluteCoords.y + delta.y) - startY,
      r = Math.sqrt(x2 * x2 + y2 * y2);
    let angle = (Math.atan2(y2, x2) / Math.PI * 180);
    angle = (angle) % 360 + 180;
    angle = (((angle + 22.4) % 360) / 45) * 45;
    angle -= 180;
    const cosx = r * Math.cos(angle * Math.PI / 180),
      sinx = r * Math.sin(angle * Math.PI / 180);
    return this._container.getRelativeCoords(
      cosx + startX,
      sinx + startY
    );
  }

  /**
   * Handle mouse up
   *
   * @protected
   */
  protected _handleMouseUp() {
    this._segment = null;
    this._isMouseDown = false;
    _preventMoving = false;
    if (this.self._command.currentCommand) {
      this.self._command.addToHistory(this.self._command.currentVersion);
      this.self._command.currentCommand = null;
    }
    this.self.emit('unselectVirtualMarker');
  }

  /**
   * Getter lockimage
   *
   * @returns {*}
   */
  public get lockimage() {
    return this._lockimage;
  }

  /**
   * Setter lockimage
   *
   * @param lockimage
   */
  public set lockimage(lockimage) {
    this._lockimage = lockimage;
  }
}
